import { HttpStatusCodeError } from '../errors/http-status-code-error';
import { InvalidVaultError } from '../errors/invalid-vault-error';
import { deserializeEntry, serializeEntry } from './vault-repository-serializer';
export class HttpVaultRepository {
    constructor(http, localStorage, logger) {
        this.http = http;
        this.localStorage = localStorage;
        this.logger = logger;
    }
    async loadVaultEntries(companyId) {
        let lastResponse = null;
        try {
            // resolve entries from local storage
            try {
                this.logger.logInformation('HTTP Vault Repository: Trying to resolve entries from local storage...');
                lastResponse = this.localStorage.get(this.getLocalStorageKey(companyId));
                if (lastResponse && lastResponse.length > 0) {
                    // make sure entries are in order
                    lastResponse.sort((a, b) => a.id - b.id);
                    // compute client version
                    const clientLastEntryResponse = lastResponse[lastResponse.length - 1];
                    this.logger.logInformation(`HTTP Vault Repository: Vault was found on client with version '${clientLastEntryResponse.blockchainSignature}'.`);
                    // get entries from server
                    this.logger.logInformation(`HTTP Vault Repository: Getting entries after client version from server...`);
                    const getEntriesResponse = await this.getEntriesAfter(clientLastEntryResponse.id, clientLastEntryResponse.blockchainSignature);
                    if (getEntriesResponse !== null) {
                        getEntriesResponse.sort((a, b) => a.id - b.id);
                    }
                    if (!getEntriesResponse) {
                        // couldn't find any vault entries on server
                        this.logger.logInformation(`HTTP Vault Repository: Server version was not found. Empty vault is loaded.`);
                        return { entries: [], cachedEntryCount: 0 };
                    }
                    else if (getEntriesResponse.length === 0) {
                        // no new entries on server
                        this.logger.logInformation(`HTTP Vault Repository: Client version matches server version. Using Vault from client.`);
                        return { entries: lastResponse.map(item => deserializeEntry(item)), cachedEntryCount: lastResponse.length };
                    }
                    else {
                        if (getEntriesResponse.some(entry => entry.id === 0)) {
                            if (getEntriesResponse[0].blockchainSignature === lastResponse[0].blockchainSignature) {
                                throw new InvalidVaultError('Invalid Vault Error: Full vault was returned, but it contains less entries. Vault is invalid.');
                            }
                            this.logger.logInformation(`HTTP Vault Repository: Full vault was returned. There were probably a hard reset.`);
                            return { entries: getEntriesResponse.map(item => deserializeEntry(item)), cachedEntryCount: 0 };
                        }
                        const serverLastEntryResponse = getEntriesResponse[getEntriesResponse.length - 1];
                        this.logger.logInformation(`HTTP Vault Repository: Version on server is '${serverLastEntryResponse.blockchainSignature}'.`);
                        // merge newer entries with local entries
                        const mergeResponse = [...lastResponse, ...getEntriesResponse];
                        return { entries: mergeResponse.map(item => deserializeEntry(item)), cachedEntryCount: lastResponse.length };
                    }
                }
            }
            catch (ex) {
                if (ex instanceof InvalidVaultError) {
                    throw ex;
                }
                this.logger.logWarning(`HTTP Vault Repository: ${ex}`);
            }
            // query up to date vault from backend
            this.logger.logInformation('HTTP Vault Repository: Getting entries from backend...');
            const response = await this.http.get('entries');
            const mapped = response.map(item => deserializeEntry(item)).sort((a, b) => a.id - b.id);
            this.logger.logInformation('HTTP Vault Repository: Entries successfully loaded.');
            return { entries: mapped, cachedEntryCount: 0 };
        }
        catch (ex) {
            this.logger.logWarning(`HTTP Vault Repository: ${ex}`);
            // return last known result
            if (lastResponse !== null && !(ex instanceof InvalidVaultError)) {
                this.logger.logInformation('HTTP Vault Repository: Returning last known result.');
                return { entries: lastResponse.map(item => deserializeEntry(item)), cachedEntryCount: lastResponse.length };
            }
            throw ex;
        }
    }
    addVaultEntry(data) {
        const request = serializeEntry(data);
        return this.http.post('entries', request);
    }
    addVaultEntries(data) {
        const request = data.map(r => serializeEntry(r));
        return this.http.post('entries/add', request);
    }
    saveVaultEntriesInLocalStorage(companyId, entries) {
        this.localStorage.set(this.getLocalStorageKey(companyId), entries.map(entry => serializeEntry(entry)));
    }
    resetVaultInLocalStorage(companyId) {
        this.localStorage.remove(this.getLocalStorageKey(companyId));
    }
    getLocalStorageKey(companyId) {
        return `ZeroTrust_Vault_${companyId}`;
    }
    async getEntriesAfter(afterEntryId, lastKnownSignature) {
        try {
            const urlParams = new URLSearchParams({ afterEntryId: afterEntryId.toString(), lastKnownSignature });
            return await this.http.get(`entries?${urlParams.toString()}`);
        }
        catch (error) {
            if (error instanceof HttpStatusCodeError && error.status === 404) {
                return null;
            }
            throw error;
        }
    }
}
